import React, { useRef, useEffect } from 'react';
import VexFlowLoader from '../utils/vexflowLoader';

// Map note names to VexFlow note names and octaves
const noteMap = {
  'Low G': { key: 'g', octave: 4, duration: 'q' },  // G on second line from bottom
  'Low A': { key: 'a', octave: 4, duration: 'q' },  // A in second space from bottom
  'B': { key: 'b', octave: 4, duration: 'q' },      // B on third line
  'C': { key: 'c', octave: 5, duration: 'q' },      // C in third space
  'D': { key: 'd', octave: 5, duration: 'q' },      // D on fourth line
  'E': { key: 'e', octave: 5, duration: 'q' },      // E in fourth space
  'F': { key: 'f', octave: 5, duration: 'q' },      // F on top line
  'High G': { key: 'g', octave: 5, duration: 'q' }, // High G above the staff
  'High A': { key: 'a', octave: 5, duration: 'q' }  // High A above the staff
};

// Internal component that receives VexFlow and renders the staff
const StaffRenderer = ({ vexflow, noteName }) => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (vexflow && containerRef.current) {
      // Clear previous content
      if (containerRef.current.innerHTML !== '') {
        containerRef.current.innerHTML = '';
      }

      try {
        // Set canvas dimensions and padding
        const canvasWidth = 220;
        const canvasHeight = 150;
        const staffWidth = 150;
        const staffY = 20; // Empirically chosen for vertical centering

        // Create and configure the renderer
        const { Renderer, Stave, StaveNote, Voice, Formatter } = vexflow;
        
        const renderer = new Renderer(containerRef.current, Renderer.Backends.SVG);
        renderer.resize(canvasWidth, canvasHeight);
        const context = renderer.getContext();
        context.setFont('Arial', 10);

        // Create a stave centered in the canvas
        const stave = new Stave(35, staffY, staffWidth);
        stave.addClef('treble');
        stave.setContext(context).draw();
        
        // Only add note if we have a valid note name
        if (noteName && noteMap[noteName]) {
          // Get note data
          const { key, octave, duration } = noteMap[noteName];
          
          const note = new StaveNote({
            keys: [`${key}/${octave}`],
            duration: duration,
            auto_stem: true // Let VexFlow determine stem direction
          });
          
          // Draw the note
          const voice = new Voice()
            .setStrict(false) // Allow partial bars
            .addTickables([note]);
          
          new Formatter()
            .joinVoices([voice])
            .formatToStave([voice], stave, { align_rests: true });
          
          voice.draw(context, stave);
        }
      } catch (error) {
        console.error('Error rendering note staff:', error);
      }
    }
  }, [vexflow, noteName]);
  
  return <div ref={containerRef} className="note-staff" />;
};

function NoteStaff({ noteName }) {
  return (
    <VexFlowLoader fallback={<div className="loading-note">Loading...</div>}>
      {(vexflow) => <StaffRenderer vexflow={vexflow} noteName={noteName} />}
    </VexFlowLoader>
  );
}

export default NoteStaff;
